import React, { useState } from 'react';
import { ExternalLink, Play, FileText, Calendar, Filter, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { usePortfolio } from '../context/PortfolioContext';

const InsightsPage = () => {
  const { talks, media, loading } = usePortfolio();
  const [ref1, isVisible1] = useScrollAnimation(0.1);
  const [ref2, isVisible2] = useScrollAnimation(0.1);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const mediaList = media || [];
  const talksList = talks || [];

  const filteredMedia = selectedCategory === 'all' 
    ? mediaList 
    : selectedCategory === 'featured'
    ? mediaList.filter(item => item.featured)
    : mediaList;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="insights-page">
      <SEO 
        title="Insights & Media | Mir Shahrukh Islam"
        description="Speaking engagements, interviews, and articles on entrepreneurship, IoT, and digital transformation in emerging markets."
        url="/insights"
      />
      <Header />
      
      {/* Hero Section with Featured Video */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-20 w-72 h-72 bg-blue-100/30 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-purple-100/20 rounded-full blur-3xl" />
        </div>

        <motion.div 
          className="max-w-7xl mx-auto px-6 relative z-10"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div>
              <motion.div variants={itemVariants} className="mb-4">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-full text-sm font-medium">
                  <Sparkles size={16} className="text-amber-400" />
                  Media & Speaking
                </span>
              </motion.div>
              <motion.h1 
                variants={itemVariants}
                className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
              >
                Insights & Media
              </motion.h1>
              <motion.p 
                variants={itemVariants}
                className="text-xl text-gray-700 leading-relaxed"
              >
                Sharing perspectives on entrepreneurship, technology, IoT, and building impactful ventures in emerging markets through speaking engagements, interviews, and written content.
              </motion.p>
            </div>

            {/* Featured Video */}
            <motion.div 
              variants={itemVariants}
              className="relative"
            >
              <a 
                href="https://www.youtube.com/watch?v=tpSvI81cHs0"
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://img.youtube.com/vi/tpSvI81cHs0/maxresdefault.jpg"
                    alt="The Bondstein Story"
                    className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                      <Play className="text-gray-900 ml-1" size={32} />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-white font-semibold text-lg">The Bondstein Story</p>
                    <p className="text-gray-300 text-sm">Watch the full conversation</p>
                  </div>
                </div>
              </a>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Speaking Engagements Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Speaking Engagements</h2>
            <p className="text-lg text-gray-700">Talks, panels, and keynotes on technology, entrepreneurship, and innovation.</p>
          </motion.div>

          <div ref={ref1} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {talksList.map((talk, index) => (
              <div
                key={talk.id}
                className={`${
                  isVisible1 ? 'animate-fade-in-up' : 'animate-on-scroll'
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <Card className="border-gray-200 hover:shadow-2xl transition-all duration-500 overflow-hidden group h-full transform hover:-translate-y-2">
                  {/* Thumbnail */}
                  <div className="relative aspect-video bg-gray-200 overflow-hidden">
                    <img
                      src={talk.thumbnail}
                      alt={talk.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Play className="text-white ml-1" size={32} />
                      </div>
                    </div>
                    {talk.featured && (
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-gray-900 text-white">Featured</Badge>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <CardContent className="p-6">
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 group-hover:text-gray-700 transition-colors">
                        {talk.title}
                      </h3>
                      <p className="text-sm text-gray-700 font-medium">{talk.event}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-2">
                        <Calendar size={14} />
                        {talk.date}
                      </p>
                      <p className="text-sm text-gray-700 leading-relaxed line-clamp-2">
                        {talk.description}
                      </p>
                      {talk.videoUrl && (
                        <a
                          href={talk.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors mt-2"
                        >
                          Watch Talk
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Media Coverage Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="mb-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Media Coverage & Articles</h2>
              <p className="text-lg text-gray-700">Featured interviews, articles, and news coverage.</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  selectedCategory === 'all'
                    ? 'bg-gray-900 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
                }`}
                data-testid="filter-all"
              >
                All Articles
              </button>
              <button
                onClick={() => setSelectedCategory('featured')}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  selectedCategory === 'featured'
                    ? 'bg-gray-900 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
                }`}
                data-testid="filter-featured"
              >
                Featured
              </button>
            </div>
          </motion.div>

          <div ref={ref2} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredMedia.map((article, index) => (
              <div
                key={article.id}
                className={`${
                  isVisible2 ? 'animate-fade-in-up' : 'animate-on-scroll'
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <Card className="border-gray-200 hover:shadow-2xl transition-all duration-500 overflow-hidden group h-full transform hover:-translate-y-2 bg-white">
                  {/* Thumbnail */}
                  <div className="relative aspect-video bg-gray-200 overflow-hidden">
                    <img
                      src={article.thumbnail}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {article.featured && (
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-gray-900 text-white">Featured</Badge>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <CardContent className="p-6">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FileText size={14} />
                        {article.publication}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 group-hover:text-gray-700 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-gray-600 flex items-center gap-2">
                        <Calendar size={14} />
                        {article.date}
                      </p>
                      <p className="text-sm text-gray-700 leading-relaxed line-clamp-3">
                        {article.description}
                      </p>
                      <a
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors mt-2"
                      >
                        Read Article
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InsightsPage;
